import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Responsive styles and modal animation
const footerStyles = `
  @keyframes sfFooterModalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  /* 4-column grid: Brand · Shop · Customer Care · Get in Touch.
     Brand column gets a touch more breathing room (1.3fr) since it
     holds the logo + tagline + socials and reads as the anchor. */
  .sf-footer-grid {
    display: grid;
    grid-template-columns: 1.3fr 1fr 1fr 1fr;
    gap: 2.5rem;
  }

  /* Column header pattern: small brand-color dot + uppercase
     tracked label. The dot is rendered via a span so its color
     can come from the merchant's brand inline. */
  .sf-footer-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--sf-text);
    margin: 0 0 0.25rem 0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .sf-footer-heading-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    /* background comes inline from themeStyles.primary */
  }

  /* Link hover micro-motion — small right-shift + color shift.
     Subtle, but it makes the column feel responsive instead of
     dead. */
  .sf-footer-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4375rem;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--sf-text-muted);
    font-weight: 500;
    text-align: left;
    text-decoration: none;
    line-height: 1.8;
    transition: color 0.15s ease, transform 0.15s ease;
    /* defeat globals.css button width override on <button> links */
    width: auto !important;
    min-width: 0 !important;
    max-width: max-content !important;
  }

  .sf-footer-link:hover {
    transform: translateX(3px);
    /* color comes inline via mouseenter handlers (uses primary) */
  }

  .sf-footer-bottom-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .sf-footer-backtop {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: 1px solid var(--sf-border);
    padding: 0.375rem 0.75rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--sf-text-muted);
    cursor: pointer;
    font-family: inherit;
    transition: color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
    width: auto !important;
    min-width: 0 !important;
    max-width: max-content !important;
  }

  .sf-footer-backtop:hover {
    transform: translateY(-1px);
    /* color + border-color come inline (brand primary on hover) */
  }

  .sf-footer-social-link {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
  }

  .sf-footer-social-link svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  @media (max-width: 1024px) {
    .sf-footer-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 2rem !important;
    }
  }

  @media (max-width: 640px) {
    .sf-footer-grid {
      grid-template-columns: 1fr !important;
      gap: 2rem !important;
      text-align: center !important;
    }
    .sf-footer-column {
      align-items: center !important;
    }
    .sf-footer-heading {
      justify-content: center !important;
    }
    .sf-footer-social-row {
      justify-content: center !important;
    }
    .sf-footer-contact-item {
      justify-content: center !important;
    }
    .sf-footer-bottom-bar {
      justify-content: center !important;
      text-align: center !important;
    }
    .sf-footer-modal-card {
      max-width: 100% !important;
      margin: 0 16px !important;
      max-height: 90vh !important;
    }
  }
`;

// Helper to check if we're on a subdomain
const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

// Inline modal component for footer content
const FooterContentModal = ({ isOpen, onClose, title, content }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'sfFooterModalFadeIn 0.25s ease-out'
      }}
    >
      <div
        className="sf-footer-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--sf-bg)',
          color: 'var(--sf-text)',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          animation: 'sfFooterModalFadeIn 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid var(--sf-border)',
          flexShrink: 0
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '700',
            color: 'var(--sf-text)'
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--sf-text-muted)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--sf-surface-2)';
              e.currentTarget.style.color = 'var(--sf-text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--sf-text-muted)';
            }}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        {/* Body */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          <div style={{
            fontSize: '0.95rem',
            lineHeight: '1.7',
            color: 'var(--sf-text)',
            whiteSpace: 'pre-wrap'
          }}>
            {content}
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = ({ theme, themeStyles, store }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');

  const onSubdomain = isOnSubdomain();

  const getStorePath = (path = '') => {
    if (onSubdomain) {
      return path ? `/${path}` : '/';
    } else {
      return path ? `/s/${store?.slug}/${path}` : `/s/${store?.slug}`;
    }
  };

  const openContentModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTitle('');
    setModalContent('');
  };

  const normalizeSocialUrl = (platform, value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.includes('.')) return `https://${trimmed}`;

    const handle = trimmed.replace(/^@/, '');
    const platformUrls = {
      instagram: `https://instagram.com/${handle}`,
      facebook: `https://facebook.com/${handle}`,
      twitter: `https://x.com/${handle}`,
      linkedin: trimmed.includes('linkedin.com') ? `https://${trimmed}` : `https://linkedin.com/company/${handle}`,
      tiktok: `https://tiktok.com/@${handle}`,
      youtube: trimmed.startsWith('@') ? `https://youtube.com/${trimmed}` : `https://youtube.com/@${handle}`,
    };

    return platformUrls[platform] || trimmed;
  };

  const configuredSocialLinks = store?.settings?.socialLinks || store?.settings?.socialMedia || store?.socialMedia || {};

  // Social links
  const socialLinks = [
    { name: 'Facebook', href: normalizeSocialUrl('facebook', configuredSocialLinks.facebook), icon: 'facebook' },
    { name: 'X', href: normalizeSocialUrl('twitter', configuredSocialLinks.twitter), icon: 'twitter' },
    { name: 'Instagram', href: normalizeSocialUrl('instagram', configuredSocialLinks.instagram), icon: 'instagram' },
    { name: 'LinkedIn', href: normalizeSocialUrl('linkedin', configuredSocialLinks.linkedin), icon: 'linkedin' },
    { name: 'TikTok', href: normalizeSocialUrl('tiktok', configuredSocialLinks.tiktok), icon: 'tiktok' },
    { name: 'YouTube', href: normalizeSocialUrl('youtube', configuredSocialLinks.youtube), icon: 'youtube' }
  ].filter(s => s.href);

  const getSocialIcon = (iconName) => {
    const icons = {
      facebook: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      twitter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      ),
      instagram: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z"/>
        </svg>
      ),
      linkedin: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      tiktok: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      ),
      youtube: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a2.98 2.98 0 00-2.098-2.108C19.548 3.58 12 3.58 12 3.58s-7.548 0-9.4.498A2.98 2.98 0 00.502 6.186C0 8.046 0 11.93 0 11.93s0 3.884.502 5.744a2.98 2.98 0 002.098 2.108c1.852.498 9.4.498 9.4.498s7.548 0 9.4-.498a2.98 2.98 0 002.098-2.108C24 15.814 24 11.93 24 11.93s0-3.884-.502-5.744zM9.545 15.45V8.41l6.273 3.52-6.273 3.52z"/>
        </svg>
      )
    };
    return icons[iconName] || icons.facebook;
  };

  // Footer links from settings or fallback to About page link
  const showAboutPage = store?.settings?.storefrontDisplay?.showAboutPage !== false;
  const footerLinks = store?.settings?.footerLinks?.filter(link => link.enabled !== false) || [];
  const aboutPageLink = store?.settings?.aboutPageLink || getStorePath('about');

  // Primary location address/contact details are explicitly public. Do not
  // fall back to the merchant's internal order-notification email.
  const primaryLocation = store?.locations?.[0];
  const supportEmail = store?.settings?.supportEmail || primaryLocation?.email;
  const phone = store?.phone || primaryLocation?.phone;
  const hasWhatsApp = store?.whatsappOrdersEnabled && store?.whatsappNumber;

  // Store name and initial
  const storeName = store?.name || 'My Store';
  const storeInitial = storeName.charAt(0).toUpperCase();
  // Description: prefer the onboarding-collected `description`, fall
  // back to the hero copy and any legacy `tagline`. Covers stores
  // that only filled in one of the fields.
  const storeDescription = store?.description || store?.heroDescription || store?.tagline;

  // Primary location address — collected at onboarding and surfaced
  // in the Customer Care column. Fields may be partial (e.g., only
  // city set), so we filter falsy parts before joining.
  const addressLine = primaryLocation
    ? [
        primaryLocation.address,
        primaryLocation.city,
        primaryLocation.state,
        primaryLocation.country
      ].filter(Boolean).join(', ')
    : '';

  return (
    <>
      <style>{footerStyles}</style>
      <footer className="sf-footer" style={{
        background: 'var(--sf-surface-2)',
        color: 'var(--sf-text)',
        marginTop: 'auto',
        borderTop: '1px solid var(--sf-border)'
      }}>
        {/* Main Footer Content */}
        <div className="sf-footer-inner" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '3rem 1.5rem 1.5rem'
        }}>
          <div className="sf-footer-grid">
            {/* Column 1: Store Info */}
            <div className="sf-footer-column" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Brand mark + name. When the merchant uploaded a logo,
                  we render the actual image with no backdrop — PNG
                  transparency stays transparent, so brand marks designed
                  for layered display keep working in both light and dark
                  mode. The monogram tile only fires as a fallback for
                  stores that haven't uploaded a logo yet. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {store?.logo ? (
                  <img
                    src={store.logo}
                    alt={storeName}
                    style={{
                      height: '40px',
                      width: 'auto',
                      maxWidth: '120px',
                      objectFit: 'contain',
                      flexShrink: 0
                    }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: themeStyles.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: '#ffffff',
                    flexShrink: 0
                  }}>
                    {storeInitial}
                  </div>
                )}
                <span style={{
                  fontSize: '1.15rem',
                  fontWeight: '700',
                  color: 'var(--sf-text)'
                }}>
                  {storeName}
                </span>
              </div>

              {/* Description */}
              {storeDescription && (
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--sf-text-muted)',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {storeDescription}
                </p>
              )}

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="sf-footer-social-row" style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginTop: '0.25rem'
                }}>
                  {socialLinks.map((social) => (
                    <a
                      className="sf-footer-social-link"
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      style={{
                        color: 'var(--sf-text-muted)',
                        textDecoration: 'none',
                        padding: 0,
                        borderRadius: '999px',
                        transition: 'color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
                        border: '1px solid var(--sf-border)',
                        backgroundColor: 'var(--sf-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = themeStyles.primary;
                        e.currentTarget.style.borderColor = themeStyles.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--sf-text-muted)';
                        e.currentTarget.style.borderColor = 'var(--sf-border)';
                      }}
                    >
                      {getSocialIcon(social.icon)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Column 2: Shop — navigation shortcuts so visitors at
                the bottom of the page can keep browsing without
                scrolling back to the header. Mirrors what the
                floating pill nav surfaces. */}
            <div className="sf-footer-column" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <h3 className="sf-footer-heading">
                <span
                  className="sf-footer-heading-dot"
                  style={{ backgroundColor: themeStyles.primary }}
                  aria-hidden="true"
                />
                Shop
              </h3>

              {[
                { label: 'Home',  path: '' },
                { label: 'Shop',  path: 'shop' },
                ...(showAboutPage ? [{ label: 'About', path: 'about' }] : [])
              ].map((item) => (
                <Link
                  key={item.label}
                  to={getStorePath(item.path)}
                  className="sf-footer-link"
                  onMouseEnter={(e) => { e.currentTarget.style.color = themeStyles.primary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--sf-text-muted)'; }}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Column 3: Customer Care */}
            <div className="sf-footer-column" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <h3 className="sf-footer-heading">
                <span
                  className="sf-footer-heading-dot"
                  style={{ backgroundColor: themeStyles.primary }}
                  aria-hidden="true"
                />
                Customer Care
              </h3>

              {/* Merchant's primary-location address — auto-pulled
                  from onboarding. Sits at the top of Customer Care so
                  it never reads as empty when a merchant hasn't yet
                  configured any policy links. */}
              {addressLine && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.4375rem',
                  fontSize: '0.875rem',
                  color: 'var(--sf-text-muted)',
                  lineHeight: '1.5'
                }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    style={{ flexShrink: 0, marginTop: '4px' }}
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{addressLine}</span>
                </div>
              )}

              {footerLinks.length > 0 ? (
                footerLinks.map((link, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => openContentModal(link.title, link.content)}
                    className="sf-footer-link"
                    onMouseEnter={(e) => { e.currentTarget.style.color = themeStyles.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--sf-text-muted)'; }}
                  >
                    {link.title}
                  </button>
                ))
              ) : (
                showAboutPage && !addressLine && (
                  <Link
                    to={aboutPageLink}
                    className="sf-footer-link"
                    onMouseEnter={(e) => { e.currentTarget.style.color = themeStyles.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--sf-text-muted)'; }}
                  >
                    About
                  </Link>
                )
              )}
            </div>

            {/* Column 4: Get in Touch */}
            <div className="sf-footer-column" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <h3 className="sf-footer-heading">
                <span
                  className="sf-footer-heading-dot"
                  style={{ backgroundColor: themeStyles.primary }}
                  aria-hidden="true"
                />
                Get in Touch
              </h3>

              {supportEmail && (
                <a
                  href={`mailto:${supportEmail}`}
                  className="sf-footer-contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--sf-text-muted)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'color 0.15s ease',
                    lineHeight: '1.8'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = themeStyles.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--sf-text-muted)';
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="2" y="4" width="20" height="16" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 4l-10 8L2 4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {supportEmail}
                </a>
              )}

              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="sf-footer-contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--sf-text-muted)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'color 0.15s ease',
                    lineHeight: '1.8'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = themeStyles.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--sf-text-muted)';
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {phone}
                </a>
              )}

              {hasWhatsApp && (
                <a
                  href={`https://wa.me/${store.whatsappNumber.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-footer-contact-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--sf-text-muted)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'color 0.15s ease',
                    lineHeight: '1.8'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#25D366';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--sf-text-muted)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              )}

              {!supportEmail && !phone && !hasWhatsApp && (
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--sf-text-muted)',
                  margin: 0,
                  lineHeight: '1.8'
                }}>
                  Contact information coming soon.
                </p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div style={{
            height: '1px',
            background: 'var(--sf-border)',
            margin: '2rem 0 1.25rem'
          }} />

          {/* Bottom Bar */}
          <div className="sf-footer-bottom-bar">
            <span style={{
              color: 'var(--sf-text-muted)',
              fontSize: '0.8125rem',
              fontWeight: '500'
            }}>
              &copy; {new Date().getFullYear()} {storeName}
            </span>

            <button
              type="button"
              className="sf-footer-backtop"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = themeStyles.primary;
                e.currentTarget.style.borderColor = themeStyles.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--sf-text-muted)';
                e.currentTarget.style.borderColor = 'var(--sf-border)';
              }}
              aria-label="Scroll back to top of the page"
            >
              Back to top
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>

            {/* Same `branding.remove_badge` gate as the floating
                <PoweredByRevstar /> FAB. Both used to use independent
                checks; some upgraded merchants saw the FAB go away but
                the footer text stay (customer complaint). Single source
                of truth: store.canRemoveBadge (resolved server-side
                from the merchant's tier feature access). */}
            {!store?.canRemoveBadge && (
              <span style={{
                color: 'var(--sf-text-muted)',
                fontSize: '0.8125rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}>
                Powered by
                <span style={{ color: themeStyles.primary, fontWeight: '700' }}>
                  Revstar
                </span>
              </span>
            )}
          </div>
        </div>
      </footer>

      {/* Footer Content Modal */}
      <FooterContentModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalTitle}
        content={modalContent}
      />
    </>
  );
};

export default Footer;
